// Persistent storage for the HIBANE Desk admin session token.
//
// History: tokens were previously stored in sessionStorage, which is wiped when
// the browser/tab/PWA closes. That forced users to re-enter credentials every
// time they reopened the app, even though their underlying Supabase auth session
// (in localStorage) was still valid. We now persist in localStorage so reopening
// the app lands the user directly on the dashboard, and ALSO mirror to
// sessionStorage so that legacy callsites that still read
// `sessionStorage.getItem('admin_session_token')` keep working. Server-side
// validation (`admin-auth-validate`) still runs on every protected route mount,
// so revoked or expired sessions are still rejected — only the lifetime of the
// convenience token changed.

export const SESSION_TOKEN_KEY = "admin_session_token";
export const SESSION_EXPIRES_KEY = "admin_session_expires";

const ADMIN_SESSION_KEYS = new Set<string>([SESSION_TOKEN_KEY, SESSION_EXPIRES_KEY]);

function safeLocal(): Storage | null {
  try { return window.localStorage; } catch { return null; }
}
function safeSession(): Storage | null {
  try { return window.sessionStorage; } catch { return null; }
}

/**
 * Read an admin-session value. Reads localStorage first, falls back to
 * sessionStorage so in-flight users (who logged in before this change) are
 * not kicked out on first reload.
 */
export function getAdminSessionItem(key: string): string | null {
  const ls = safeLocal()?.getItem(key);
  if (ls) return ls;
  return safeSession()?.getItem(key) ?? null;
}

/**
 * Persist an admin-session value across browser restarts.
 *
 * We write to BOTH localStorage (so reopening the browser/PWA keeps the user
 * signed in) AND sessionStorage. The sessionStorage mirror is required because
 * a large number of legacy callsites (social-media hooks, voice-agent, MFA,
 * invoice editors, etc.) still read `sessionStorage.getItem('admin_session_token')`
 * directly. Without the mirror, those callsites get `null` and every admin
 * edge-function call short-circuits with "No session token found", which
 * manifests as empty Connected Accounts, zero post counts, and similar bugs.
 */
export function setAdminSessionItem(key: string, value: string): void {
  safeLocal()?.setItem(key, value);
  safeSession()?.setItem(key, value);
}

/** Remove an admin-session value from both stores. */
export function removeAdminSessionItem(key: string): void {
  safeLocal()?.removeItem(key);
  safeSession()?.removeItem(key);
}

/** Clear all known admin session keys plus any auxiliary admin-session flags. */
export function clearAdminSessionStore(extraKeys: string[] = []): void {
  for (const k of [...ADMIN_SESSION_KEYS, ...extraKeys]) {
    removeAdminSessionItem(k);
  }
}

// One-time bootstrap (runs at module import): for users who logged in BEFORE
// the sessionStorage-mirror change shipped, their token lives only in
// localStorage. Mirror it into sessionStorage now so legacy callsites that read
// `sessionStorage.getItem('admin_session_token')` work immediately, without
// requiring those users to log out and back in.
(function mirrorLocalToSessionOnLoad() {
  try {
    const ls = safeLocal();
    const ss = safeSession();
    if (!ls || !ss) return;
    for (const k of ADMIN_SESSION_KEYS) {
      const v = ls.getItem(k);
      if (v && !ss.getItem(k)) ss.setItem(k, v);
    }
  } catch {
    // Storage may be unavailable (SSR, privacy mode); safe to ignore.
  }
})();
